import {
  ErrorModal,
  HelmetDexteel,
  useUserContext,
  useHasPermission,
} from "@dexteel/mesf-core";
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Build as BuildIcon,
} from "@mui/icons-material";
import {
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Button,
  Paper,
  TextField,
} from "@mui/material";
import { Group, Panel } from "react-resizable-panels";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { LazyBackDropLoading } from "@/controls/LazyLoading";
import {
  BurnerComponentSelector,
  type BurnerSelectableComponent,
  SidewallTreePanel,
  SidewallMapPanel,
  SetShellOnlineModal,
} from "./components";
import { SidewallSvgThumbnail, type SidewallData } from "./components/map-view";
import {
  MetricsPanel,
  HistoryPanel,
  DetailsPanel,
  ViewToggle,
  NoPermissionModal,
  type ViewMode,
} from "../../shared/components";
import {
  InstallPartModal,
  EditPartModal,
  NewSparePartModal,
  NotesModal,
  SetInitialMetricModal,
  CorrectDatesModal,
  SetEquipmentStatusModal,
  DeleteConfirmModal,
} from "../../shared/components/modals";
import {
  getPartDetails as getPartDetailsSidewall,
  getTaskMetadata,
} from "./repositories/SidewallRepository";
import type { PartInfo } from "../../shared/models";

import type {
  SidewallTreeRow,
  PartDetailsMasterAPI,
  PartDetailsHistoryAPI,
} from "./models";
import {
  PART_IDS,
  PART_TYPE_IDS,
  isSpareContainer,
  isShellType,
  isBurnerSubComponent,
  BURNER_POSITIONS,
  DEFAULT_BURNER_CHILD_POSITION_ID,
  WATER_JACKET_POSITION_ID,
  isUpperShellChild,
  isLowerShellChild,
  transformHistoryAPIToEntries,
} from "./models";
import { useResizablePanels } from "../../../sy/hooks/useResizablePanels";
import { ResizablePanelSeparator } from "../../../sy/components/ResizablePanelSeparator";
import { useVerticalDragResize } from "../../../sy/hooks/useVerticalDragResize";
import { VerticalDragSeparator } from "../../../sy/components/VerticalDragSeparator";
import { getPartDetailsParams } from "../../shared/utils/getPartDetailsParams";
import {
  usePartDetailsQuery,
  type GetPartDetailsFn,
} from "../../shared/hooks/queries";
import type { PartDetailsParams } from "../../shared/utils/getPartDetailsParams";
import {
  useSidewallPartsTreeQuery,
  useRealtimeInvalidation,
} from "./hooks/queries";
import {
  useSetSidewallOnlineMutation,
  useSetSidewallOfflineMutation,
  useInstallSidewallPartMutation,
  useInstallExistingPartMutation,
  useUninstallSidewallPartMutation,
  useRepairSidewallPartMutation,
  useUpsertSidewallPartDetailsMutation,
  useCreateSparePartMutation,
  useDeleteSparePartMutation,
} from "./hooks/mutations";
import {
  useSetInitialMetricValueMutation,
  useUpdateTaskTimestampMutation,
} from "../../shared/hooks/mutations";
import { useSetEquipmentOnlineOffline } from "../../shared/hooks/useSetEquipmentOnlineOffline";
import { getOperationBounds } from "../../shared/repositories/SharedEqpRepository";
// Cast the sidewall-specific getPartDetails to the shared generic type.
// The runtime shapes are compatible — this cast bridges the typed rows vs Record<string, unknown>.
const getPartDetails = getPartDetailsSidewall as unknown as GetPartDetailsFn;

/** Extract error message from an unknown error (TanStack Query default error type). */
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return "An unexpected error occurred";
};

/**
 * After the slot→shell restructure, shells render as rowType="location" with
 * HasChildren=true and an isShellType PartTypeId. Use this helper to identify
 * shell rows (the equipment-like rows that hold panels) without confusing them
 * with regular panel positions.
 */
const isShellRow = (row: SidewallTreeRow | null | undefined): boolean =>
  !!row &&
  row.rowType === "location" &&
  row.HasChildren === true &&
  isShellType(row.PartTypeId);

/**
 * Sidewall-specific override for getPartDetailsParams.
 * Burner sub-components use BurnerPanelPartId as parentPartId instead of EquipmentId.
 */
const getSidewallPartDetailsParams = (
  row: SidewallTreeRow,
): PartDetailsParams => {
  // For burner sub-components, the parent is the BurnerPanel (604/605/606),
  // not the Shell (EquipmentId). Override the shared helper for this case.
  if (
    row.rowType === "location" &&
    row.BurnerPanelPartId &&
    row.ParentBurnerPositionId
  ) {
    return {
      partId: row.L2PartId ?? null,
      parentPartId: row.BurnerPanelPartId,
      positionId: row.PositionId ?? null,
    };
  }

  return getPartDetailsParams(row);
};

export const SidewallPage = () => {
  const { state: userState } = useUserContext();
  const hasPermission = useHasPermission();

  // Permission control (granular like Roof)
  const canSetOnline = hasPermission("RBD.EQPSidewallSetOnline");
  const canSetOffline = hasPermission("RBD.EQPSidewallSetOffline");
  const canEditTimestamp = hasPermission("EQP.UpsertTask");

  // Resizable panels (horizontal — tree vs details)
  const { groupRef, defaultLayout, onLayoutChanged, resetLayout } =
    useResizablePanels({
      groupId: "sidewall-panels",
      defaultLayout: { tree: 50, details: 50 },
    });

  // Vertical drag-to-resize for details+metrics vs history (content-adaptive)
  const { dragHeight, containerRef, handleDragStart, resetDragHeight } =
    useVerticalDragResize();

  // ==========================================================================
  // Query Hooks (replace loadData, loadHistory, loadMetrics)
  // ==========================================================================
  const {
    data: treeData = [],
    isLoading: isLoadingTree,
    refetch: refetchTree,
  } = useSidewallPartsTreeQuery();

  // Enable real-time updates via SignalR
  useRealtimeInvalidation();

  // Keep a ref in sync for callbacks that need current tree data
  const treeDataRef = useRef<SidewallTreeRow[]>([]);
  useEffect(() => {
    treeDataRef.current = treeData;
  }, [treeData]);

  // ==========================================================================
  // Mutation Hooks
  // ==========================================================================
  const setOnlineMutation = useSetSidewallOnlineMutation();
  const setOfflineMutation = useSetSidewallOfflineMutation();
  const installPartMutation = useInstallSidewallPartMutation();
  const installExistingMutation = useInstallExistingPartMutation();
  const uninstallPartMutation = useUninstallSidewallPartMutation();
  const repairPartMutation = useRepairSidewallPartMutation();
  const upsertDetailsMutation = useUpsertSidewallPartDetailsMutation();
  const createSpareMutation = useCreateSparePartMutation();
  const deleteSpareMutation = useDeleteSparePartMutation();
  const setInitialMetricMutation = useSetInitialMetricValueMutation([
    ["sidewall-tree"],
    ["sidewall-details"],
  ]);
  const updateTimestampMutation = useUpdateTaskTimestampMutation([
    ["sidewall-tree"],
    ["sidewall-details"],
  ]);

  const {
    handleSetOnline: executeSetOnline,
    handleSetOffline: executeSetOffline,
  } = useSetEquipmentOnlineOffline({
    setOnlineMutation,
    setOfflineMutation,
    findOccupant: (treeData, targetRow) =>
      // Shells are rendered as rowType="location" with isShellType. Find one currently online
      // with the same shell type (Upper or Lower) but different PartId.
      treeData.find(
        (row) =>
          row.rowType === "location" &&
          row.Status === "Online" &&
          row.L2PartId !== targetRow.EquipmentId &&
          row.PartTypeId === targetRow.PartTypeId,
      ) ?? null,
    buildOnlineParams: (targetRow, timestamp) => {
      // Find the slot PartId matching the shell's PartType
      // Upper-Shell (306) → Upper-Shell-Slot (312), Lower-Shell (311) → Lower-Shell-Slot (313)
      const targetSlotTypeId =
        targetRow.PartTypeId === PART_TYPE_IDS.UPPER_SHELL
          ? PART_TYPE_IDS.UPPER_SHELL_SLOT
          : targetRow.PartTypeId === PART_TYPE_IDS.LOWER_SHELL
            ? PART_TYPE_IDS.LOWER_SHELL_SLOT
            : null;
      const targetSlot = targetSlotTypeId
        ? treeDataRef.current.find(
            (r) =>
              r.rowType === "container" &&
              r.SlotPartTypeId === targetSlotTypeId,
          )
        : null;
      return {
        sidewallPartId: targetRow.EquipmentId,
        parentPartId: targetSlot?.ContainerId ?? PART_IDS.SIDEWALL,
        timestamp,
      };
    },
    buildOfflineParams: (occupant, timestamp) => ({
      sidewallPartId: occupant.EquipmentId,
      timestamp,
    }),
  });

  const isMutating =
    setOnlineMutation.isPending ||
    setOfflineMutation.isPending ||
    installPartMutation.isPending ||
    installExistingMutation.isPending ||
    uninstallPartMutation.isPending ||
    repairPartMutation.isPending ||
    upsertDetailsMutation.isPending ||
    createSpareMutation.isPending ||
    deleteSpareMutation.isPending;

  // ==========================================================================
  // UI State
  // ==========================================================================
  const [selectedRow, setSelectedRow] = useState<SidewallTreeRow | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("tree");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Burner sub-component selector state (for 3D view burner host selection)
  // Tracks which sub-component (Block=1, FrontFace=2, Nozzle=4) is selected via radio buttons
  const [burnerChildPositionId, setBurnerChildPositionId] = useState<number>(
    DEFAULT_BURNER_CHILD_POSITION_ID,
  );

  // Derived: Is the current selection a burner parent in map mode?
  const isBurnerParentInMapMode =
    viewMode === "map" &&
    selectedRow?.HasChildren === true &&
    !isShellType(selectedRow?.PartTypeId);

  // Derived: The active burner child row (resolves radio selection to actual tree row)
  const activeBurnerChildRow = useMemo<SidewallTreeRow | null>(() => {
    if (!isBurnerParentInMapMode || !selectedRow) return null;
    return (
      treeData.find(
        (r) =>
          r.ParentBurnerPositionId === selectedRow.PositionId &&
          r.EquipmentId === selectedRow.EquipmentId &&
          r.PositionId === burnerChildPositionId,
      ) ?? null
    );
  }, [isBurnerParentInMapMode, selectedRow, treeData, burnerChildPositionId]);

  // Derived: Burner sub-component options from tree data for the selected burner parent.
  // Falls back to static values if tree data has no sub-component rows (e.g., no BurnerPanel fixture installed).
  const burnerSelectableComponents = useMemo<
    BurnerSelectableComponent[]
  >(() => {
    if (!isBurnerParentInMapMode || !selectedRow) return [];

    const childRows = treeData.filter(
      (r) =>
        r.ParentBurnerPositionId === selectedRow.PositionId &&
        r.EquipmentId === selectedRow.EquipmentId,
    );

    if (childRows.length === 0) {
      // Fallback: no sub-component rows available (unlikely but safe)
      return [
        { positionId: 1, label: "Block" },
        { positionId: 2, label: "Front Face" },
        { positionId: 3, label: "Water Jacket" },
        { positionId: 4, label: "Nozzle" },
      ];
    }

    return childRows
      .filter(
        (r): r is SidewallTreeRow & { PositionId: number } =>
          r.PositionId != null,
      )
      .map((r) => ({
        positionId: r.PositionId,
        // Use the last path segment as the short label (e.g., "Block", "Front Face")
        label: r.path[r.path.length - 1] ?? `Position ${r.PositionId}`,
      }))
      .toSorted((a, b) => a.positionId - b.positionId);
  }, [isBurnerParentInMapMode, selectedRow, treeData]);

  // Derived: The effective row for data loading (metrics, history) and actions
  // When a burner parent is selected in map mode, use the resolved child row
  const effectiveRow = activeBurnerChildRow ?? selectedRow;

  // Modal state
  const [installModalOpen, setInstallModalOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [newSparePartModalOpen, setNewSparePartModalOpen] = useState(false);
  const [editPartModalOpen, setEditPartModalOpen] = useState(false);
  const [setInitialMetricModalOpen, setSetInitialMetricModalOpen] =
    useState(false);
  const [correctDatesModalOpen, setCorrectDatesModalOpen] = useState(false);
  const [correctDatesPartId, setCorrectDatesPartId] = useState<number | null>(
    null,
  );
  const [correctDatesPartName, setCorrectDatesPartName] = useState<
    string | null
  >(null);
  const [modalAction, setModalAction] = useState<"uninstall" | "repair" | null>(
    null,
  );
  const [actionRow, setActionRow] = useState<SidewallTreeRow | null>(null);

  // Set Status Modal state
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [shellLocationTimestamp, setShellLocationTimestamp] =
    useState<Date | null>(null);
  const [statusModalAction, setStatusModalAction] = useState<
    "online" | "offline"
  >("online");

  // Set Shell Online (from slot) modal state — used when right-clicking an
  // empty Upper/Lower Location slot to pick which shell to install.
  const [shellOnlineModalOpen, setShellOnlineModalOpen] = useState(false);
  const [shellOnlineSlotRow, setShellOnlineSlotRow] =
    useState<SidewallTreeRow | null>(null);

  // Delete Confirm Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // No Permission Modal state
  const [noPermissionModalOpen, setNoPermissionModalOpen] = useState(false);
  const [noPermissionAction, setNoPermissionAction] = useState<string | null>(
    null,
  );

  // ==========================================================================
  // Derived Data from Query Cache (for modals that need master/metrics data)
  // ==========================================================================
  const partDetailsParams = effectiveRow
    ? getSidewallPartDetailsParams(effectiveRow)
    : null;

  // Page-level query for master data (deduped with MetricsPanel/HistoryPanel)
  const partDetailsQuery = usePartDetailsQuery(
    "sidewall",
    partDetailsParams,
    getPartDetails,
  );

  // Extract master data from the query result (for MetricsPanel uninstalledOn).
  // Shells are equipment-like rows: rowType="location" + HasChildren + shell PartType.
  const selectedEquipmentMasterData: PartDetailsMasterAPI | null =
    isShellRow(effectiveRow) && partDetailsQuery.data
      ? ((partDetailsQuery.data.tables[0]
          ?.rows[0] as unknown as PartDetailsMasterAPI) ?? null)
      : null;

  // Extract metrics from the query result (for SetInitialMetricModal)
  const metrics = (partDetailsQuery.data?.tables[1]?.rows ?? []) as unknown as {
    MetricTypeId: number;
    PartialValue: number | null;
  }[];

  // Track online equipment installation dates by PartTypeId (for stale end-campaign detection).
  // Keyed by PartTypeId so Upper-Shell and Lower-Shell are compared independently.
  // Find online shells from tree data and look up their master data via query cache.
  const onlineUpperShell = treeData.find(
    (row) =>
      isShellRow(row) &&
      row.Status === "Online" &&
      row.PartTypeId === PART_TYPE_IDS.UPPER_SHELL,
  );
  const onlineLowerShell = treeData.find(
    (row) =>
      isShellRow(row) &&
      row.Status === "Online" &&
      row.PartTypeId === PART_TYPE_IDS.LOWER_SHELL,
  );

  const onlineUpperParams = onlineUpperShell
    ? getSidewallPartDetailsParams(onlineUpperShell)
    : null;
  const onlineLowerParams = onlineLowerShell
    ? getSidewallPartDetailsParams(onlineLowerShell)
    : null;

  const onlineUpperDetailsQuery = usePartDetailsQuery(
    "sidewall",
    onlineUpperParams,
    getPartDetails,
  );
  const onlineLowerDetailsQuery = usePartDetailsQuery(
    "sidewall",
    onlineLowerParams,
    getPartDetails,
  );

  const onlineInstalledOnByType = useMemo<Record<number, Date>>(() => {
    const result: Record<number, Date> = {};

    const upperMaster = onlineUpperDetailsQuery.data?.tables[0]
      ?.rows[0] as unknown as PartDetailsMasterAPI | undefined;
    if (upperMaster?.InstalledOn) {
      result[PART_TYPE_IDS.UPPER_SHELL] = new Date(upperMaster.InstalledOn);
    }

    const lowerMaster = onlineLowerDetailsQuery.data?.tables[0]
      ?.rows[0] as unknown as PartDetailsMasterAPI | undefined;
    if (lowerMaster?.InstalledOn) {
      result[PART_TYPE_IDS.LOWER_SHELL] = new Date(lowerMaster.InstalledOn);
    }

    return result;
  }, [onlineUpperDetailsQuery.data, onlineLowerDetailsQuery.data]);

  // ==========================================================================
  // Sync selected row with tree data after mutations/refetches
  // ==========================================================================
  useEffect(() => {
    if (treeData.length === 0) return;

    setSelectedRow((current) => {
      if (current) {
        // Keep selection in sync with updated tree data
        return treeData.find((row) => row.id === current.id) ?? null;
      }
      // Initial load: pre-select the first online shell (Upper/Lower)
      const onlineShell = treeData.find(
        (row) =>
          row.rowType === "location" &&
          row.Status === "Online" &&
          row.HasChildren === true,
      );
      return onlineShell ?? null;
    });
  }, [treeData]);

  // Reset vertical drag height when selection changes (re-adapt to content)
  useEffect(() => {
    resetDragHeight();
  }, [selectedRow, resetDragHeight]);

  // Handlers
  const handleRowSelection = (row: SidewallTreeRow | null) => {
    // Tree view: auto-select Block child when clicking a burner parent row
    if (
      viewMode === "tree" &&
      row?.HasChildren === true &&
      !isShellType(row?.PartTypeId)
    ) {
      const blockChild = treeData.find(
        (r) =>
          r.ParentBurnerPositionId === row.PositionId &&
          r.EquipmentId === row.EquipmentId &&
          r.PositionId === DEFAULT_BURNER_CHILD_POSITION_ID,
      );
      if (blockChild) {
        setSelectedRow(blockChild);
        setBurnerChildPositionId(DEFAULT_BURNER_CHILD_POSITION_ID);
        return;
      }
    }

    // Map view: burner sub-component clicked (including Water Jacket) → promote to parent + set radio
    if (viewMode === "map" && row && isBurnerSubComponent(row)) {
      const burnerParent = treeData.find(
        (r) =>
          r.rowType === "location" &&
          r.EquipmentId === row.EquipmentId &&
          r.PositionId === row.ParentBurnerPositionId &&
          r.HasChildren === true,
      );
      if (burnerParent) {
        setSelectedRow(burnerParent);
        setBurnerChildPositionId(
          row.PositionId ?? DEFAULT_BURNER_CHILD_POSITION_ID,
        );
        return;
      }
    }

    setSelectedRow(row);
    // Reset burner child selector to Block when selection changes
    setBurnerChildPositionId(DEFAULT_BURNER_CHILD_POSITION_ID);
  };

  // Handler for burner sub-component radio selector (3D view only)
  const handleBurnerChildChange = (positionId: number) => {
    setBurnerChildPositionId(positionId);
  };

  // Handler for view mode toggle with burner sub-component awareness
  // tree→map: burner child selected → promote to burner parent + set radio button
  // map→tree: burner parent + radio → select the actual child row in the tree
  const handleViewModeChange = (newMode: ViewMode) => {
    if (newMode === "map" && selectedRow && isBurnerSubComponent(selectedRow)) {
      // Switching to map with a burner child selected → select parent, set radio
      const burnerParent = treeData.find(
        (r) =>
          r.rowType === "location" &&
          r.EquipmentId === selectedRow.EquipmentId &&
          r.PositionId === selectedRow.ParentBurnerPositionId &&
          r.HasChildren === true,
      );
      if (burnerParent) {
        setBurnerChildPositionId(
          selectedRow.PositionId ?? DEFAULT_BURNER_CHILD_POSITION_ID,
        );
        setSelectedRow(burnerParent);
      }
    } else if (newMode === "tree" && isBurnerParentInMapMode && selectedRow) {
      // Switching to tree with a burner parent + radio → select the child row
      const childRow = treeData.find(
        (r) =>
          r.ParentBurnerPositionId === selectedRow.PositionId &&
          r.EquipmentId === selectedRow.EquipmentId &&
          r.PositionId === burnerChildPositionId,
      );
      if (childRow) {
        setSelectedRow(childRow);
      }
    }
    setViewMode(newMode);
  };

  // Show no permission modal
  const showNoPermissionModal = (actionName: string) => {
    setNoPermissionAction(actionName);
    setNoPermissionModalOpen(true);
  };

  // Handler for map panel - sets online from sidewall PartId
  const handleSetOnlineFromMap = async (sidewallPartId: number) => {
    const row = treeDataRef.current.find(
      (r) => r.EquipmentId === sidewallPartId && isShellRow(r),
    );
    if (row) {
      handleSetOnlineAction(row);
    }
  };

  // Handler for map panel - sets offline from sidewall PartId
  const handleSetOfflineFromMap = async (sidewallPartId: number) => {
    const row = treeDataRef.current.find(
      (r) => r.EquipmentId === sidewallPartId && isShellRow(r),
    );
    if (row) {
      handleSetOfflineAction(row);
    }
  };

  // Open status modal for set online action
  const handleSetOnlineAction = async (row: SidewallTreeRow) => {
    if (!canSetOnline) {
      showNoPermissionModal("set sidewall online");
      return;
    }

    // Case 1: Right-click on a slot container (Upper/Lower Location) → open
    // the shell-picker modal so the user can choose which shell to install.
    if (row.rowType === "container" && row.SlotPartTypeId != null) {
      // Pre-fetch location bounds for the target slot.
      if (row.ContainerId) {
        try {
          const bounds = await getOperationBounds(
            0,
            "install",
            row.ContainerId,
            1,
          );
          setShellLocationTimestamp(bounds.lowerBound);
        } catch {
          setShellLocationTimestamp(null);
        }
      } else {
        setShellLocationTimestamp(null);
      }
      setShellOnlineSlotRow(row);
      setShellOnlineModalOpen(true);
      return;
    }

    // Case 2: Right-click on a shell row in spares → use the existing
    // status-modal flow (target slot derived from the shell's PartType).
    const targetSlotTypeId =
      row.PartTypeId === PART_TYPE_IDS.UPPER_SHELL
        ? PART_TYPE_IDS.UPPER_SHELL_SLOT
        : row.PartTypeId === PART_TYPE_IDS.LOWER_SHELL
          ? PART_TYPE_IDS.LOWER_SHELL_SLOT
          : null;

    const targetSlot = targetSlotTypeId
      ? treeDataRef.current.find(
          (r) =>
            r.rowType === "container" && r.SlotPartTypeId === targetSlotTypeId,
        )
      : null;

    if (row.EquipmentId && targetSlot?.ContainerId) {
      try {
        const bounds = await getOperationBounds(
          row.EquipmentId,
          "install",
          targetSlot.ContainerId,
          1,
        );
        setShellLocationTimestamp(bounds.lowerBound);
      } catch {
        setShellLocationTimestamp(null);
      }
    } else {
      setShellLocationTimestamp(null);
    }

    setActionRow(row);
    setStatusModalAction("online");
    setStatusModalOpen(true);
  };

  // Open status modal for set offline action
  const handleSetOfflineAction = (row: SidewallTreeRow) => {
    if (!canSetOffline) {
      showNoPermissionModal("set sidewall offline");
      return;
    }
    setActionRow(row);
    setStatusModalAction("offline");
    setStatusModalOpen(true);
  };

  // Handle status modal confirmation
  const handleStatusModalConfirm = async (
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    if (!actionRow?.EquipmentId) return;

    try {
      if (statusModalAction === "online") {
        await executeSetOnline(treeDataRef.current, actionRow, timestamp);
        setNotificationMessage(`${actionRow.EquipmentName} is now Online`);
      } else {
        await executeSetOffline(actionRow, timestamp);
        setNotificationMessage(`${actionRow.EquipmentName} is now Offline`);
      }
      setStatusModalOpen(false);
      setActionRow(null);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  const handleInstallPart = (
    equipmentId: number,
    positionId: number,
    partTypeId: number,
    partInfo: PartInfo,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    installPartMutation.mutate(
      {
        sidewallPartId: equipmentId,
        positionId,
        partTypeId,
        partInfo,
        comments: notes,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part installed successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleInstallExistingPart = (
    existingPartId: number,
    sidewallPartId: number,
    positionId: number,
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    installExistingMutation.mutate(
      {
        existingPartId,
        sidewallPartId,
        positionId,
        comments: notes,
        operatorNumber,
        timestamp,
      },
      {
        onSuccess: () => {
          setNotificationMessage("Part installed successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Action handlers
  const handleInstallAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setInstallModalOpen(true);
  };

  const handleUninstallAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setModalAction("uninstall");
    setNotesModalOpen(true);
  };

  const handleRepairAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setModalAction("repair");
    setNotesModalOpen(true);
  };

  const handleNewSparePartAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setNewSparePartModalOpen(true);
  };

  const handleEditPartAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setEditPartModalOpen(true);
  };

  const handleSetInitialMetricAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setSelectedRow(row);
    setSetInitialMetricModalOpen(true);
  };

  const handleDeleteSparePartAction = (row: SidewallTreeRow) => {
    setActionRow(row);
    setDeleteModalOpen(true);
  };

  const handleDeleteSparePartConfirm = () => {
    if (!actionRow?.L2PartId) return;

    const deletedPartId = actionRow.L2PartId;
    deleteSpareMutation.mutate(
      { partId: deletedPartId, userId: userState.userId },
      {
        onSuccess: () => {
          setNotificationMessage("Spare part deleted successfully");
          setDeleteModalOpen(false);
          setActionRow(null);
          // Clear selection if deleted row was selected
          if (selectedRow?.L2PartId === deletedPartId) {
            setSelectedRow(null);
          }
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  // Modal confirm handlers
  const handleNotesModalConfirm = (
    notes: string | null,
    operatorNumber: number,
    timestamp: Date | null,
  ) => {
    if (!actionRow || !modalAction) return;

    const mutationCallbacks = {
      onSuccess: () => {
        const message =
          modalAction === "uninstall"
            ? "Part uninstalled successfully"
            : "Repair logged successfully";
        setNotificationMessage(message);
        setNotesModalOpen(false);
        setModalAction(null);
        setActionRow(null);
      },
      onError: (error: unknown) => {
        setErrorMessage(getErrorMessage(error));
      },
    };

    if (modalAction === "uninstall" && actionRow.L2PartId != null) {
      uninstallPartMutation.mutate(
        {
          partId: actionRow.L2PartId,
          comments: notes,
          operatorNumber,
          timestamp,
        },
        mutationCallbacks,
      );
    } else if (modalAction === "repair" && actionRow.L2PartId != null) {
      repairPartMutation.mutate(
        {
          partId: actionRow.L2PartId,
          comments: notes,
          operatorNumber,
          timestamp,
        },
        mutationCallbacks,
      );
    }
  };

  const handleNewSparePartConfirm = (partInfo: PartInfo) => {
    if (!actionRow) return;

    const containerId = actionRow.ContainerId ?? actionRow.EquipmentId;

    createSpareMutation.mutate(
      { containerId, partInfo, userId: userState.userId },
      {
        onSuccess: () => {
          setNotificationMessage("Spare part created successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleEditPartConfirm = (l2PartId: number, partInfo: PartInfo) => {
    upsertDetailsMutation.mutate(
      { partId: l2PartId, partInfo, comments: null },
      {
        onSuccess: () => {
          setNotificationMessage("Part updated successfully");
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleSetInitialMetricConfirm = (
    newPartialValue: number,
    _operatorNumber: number,
    applyToChildren: boolean,
  ) => {
    const partId = applyToChildren
      ? actionRow?.EquipmentId
      : (actionRow?.L2PartId ?? selectedRow?.L2PartId);
    if (!partId) return;

    setInitialMetricMutation.mutate(
      {
        partId,
        newValue: newPartialValue,
        applyToChildren,
        userId: userState.userId,
      },
      {
        onSuccess: (resp: {
          data?: { tables?: { rows?: Record<string, unknown>[] }[] };
        }) => {
          if (applyToChildren) {
            const firstRow = resp.data?.tables?.[0]?.rows?.[0];
            const count =
              (firstRow as { AffectedCount?: number } | undefined)
                ?.AffectedCount ?? 0;
            setNotificationMessage(`Initial metric set for ${count} parts`);
          } else {
            setNotificationMessage("Initial metric set successfully");
          }
          setSetInitialMetricModalOpen(false);
          setActionRow(null);
        },
        onError: (error: unknown) => {
          setErrorMessage(getErrorMessage(error));
        },
      },
    );
  };

  const handleCorrectDatesAction = (row: SidewallTreeRow) => {
    if (!canEditTimestamp) {
      showNoPermissionModal("correct dates");
      return;
    }
    const partId = row.L2PartId;
    if (!partId) return;
    setCorrectDatesPartId(partId);
    setCorrectDatesPartName(
      isShellRow(row)
        ? (row.EquipmentName ?? null)
        : (row.PositionName ?? row.L2PartName ?? null),
    );
    setCorrectDatesModalOpen(true);
  };

  const handleCorrectDatesSave = async (
    taskId: number,
    newTimestamp: Date,
  ): Promise<boolean> => {
    try {
      await updateTimestampMutation.mutateAsync({
        taskId,
        newTimestamp,
      });
      setNotificationMessage("Timestamp updated successfully");
      return true;
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to update timestamp",
      );
      return false;
    }
  };

  // Modal close handlers
  const handleCloseInstallModal = () => {
    setInstallModalOpen(false);
    setActionRow(null);
  };

  const handleCloseNotesModal = () => {
    setNotesModalOpen(false);
    setModalAction(null);
    setActionRow(null);
  };

  const handleCloseNewSparePartModal = () => {
    setNewSparePartModalOpen(false);
    setActionRow(null);
  };

  const handleCloseEditPartModal = () => {
    setEditPartModalOpen(false);
    setActionRow(null);
  };

  const handleCloseSetInitialMetricModal = () => {
    setSetInitialMetricModalOpen(false);
    setActionRow(null);
  };

  const handleCloseCorrectDatesModal = () => {
    setCorrectDatesModalOpen(false);
    setCorrectDatesPartId(null);
    setCorrectDatesPartName(null);
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setActionRow(null);
  };

  const handleCloseShellOnlineModal = () => {
    setShellOnlineModalOpen(false);
    setShellOnlineSlotRow(null);
    setShellLocationTimestamp(null);
  };

  /**
   * Confirm handler for SetShellOnlineModal: install the picked shell on the
   * slot. If the slot already has a shell installed (swap), the existing
   * occupant is auto-offlined first (mirrors Caster mold swap behavior).
   */
  const handleSetShellOnlineConfirm = async (
    slotPartId: number,
    shellPartId: number,
    _operatorNumber: number,
    timestamp: Date | null,
  ) => {
    try {
      // Find any shell currently installed on this slot (swap scenario).
      const occupant = treeDataRef.current.find(
        (r) =>
          isShellRow(r) &&
          r.Status === "Online" &&
          r.L2PartId !== shellPartId &&
          // Match the slot via PartType (Upper-Shell-Slot ↔ Upper-Shell)
          ((slotPartId === shellOnlineSlotRow?.ContainerId &&
            shellOnlineSlotRow?.SlotPartTypeId ===
              PART_TYPE_IDS.UPPER_SHELL_SLOT &&
            r.PartTypeId === PART_TYPE_IDS.UPPER_SHELL) ||
            (slotPartId === shellOnlineSlotRow?.ContainerId &&
              shellOnlineSlotRow?.SlotPartTypeId ===
                PART_TYPE_IDS.LOWER_SHELL_SLOT &&
              r.PartTypeId === PART_TYPE_IDS.LOWER_SHELL)),
      );

      if (occupant) {
        await setOfflineMutation.mutateAsync({
          sidewallPartId: occupant.EquipmentId,
          timestamp,
        });
      }

      // Bump the install timestamp by +1 second when swapping so it's strictly
      // after the offline task (location floor check in UpsertTask).
      const onlineTimestamp =
        occupant && timestamp
          ? new Date(timestamp.getTime() + 1000)
          : timestamp;

      await setOnlineMutation.mutateAsync({
        sidewallPartId: shellPartId,
        parentPartId: slotPartId,
        timestamp: onlineTimestamp,
      });

      setNotificationMessage(
        `Shell installed on ${shellOnlineSlotRow?.ContainerName ?? "slot"}`,
      );
      handleCloseShellOnlineModal();
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    }
  };

  // Computed: available shells in Shell Spares filtered by the slot's matching
  // shell type. Upper Location → Upper-Shell parts; Lower Location → Lower-Shell.
  const availableSpareShells = useMemo(() => {
    if (!shellOnlineModalOpen || !shellOnlineSlotRow) return [];
    const slotTypeId = shellOnlineSlotRow.SlotPartTypeId;
    const matchShellTypeId =
      slotTypeId === PART_TYPE_IDS.UPPER_SHELL_SLOT
        ? PART_TYPE_IDS.UPPER_SHELL
        : slotTypeId === PART_TYPE_IDS.LOWER_SHELL_SLOT
          ? PART_TYPE_IDS.LOWER_SHELL
          : null;
    if (matchShellTypeId == null) return [];
    // De-dupe by EquipmentId (the shell's PartId) in case the tree contains
    // multiple representations for the same shell.
    const seen = new Set<number>();
    const result: {
      partId: number;
      name: string;
      lastTaskTimestamp: Date | null;
    }[] = [];
    for (const r of treeData) {
      if (!isShellRow(r)) continue;
      if (r.Status !== "Offline") continue;
      if (r.PartTypeId !== matchShellTypeId) continue;
      if (seen.has(r.EquipmentId)) continue;
      seen.add(r.EquipmentId);
      result.push({
        partId: r.EquipmentId,
        name: r.EquipmentName ?? `Shell #${r.EquipmentId}`,
        lastTaskTimestamp: r.InstalledDate ?? null,
      });
    }
    return result;
  }, [shellOnlineModalOpen, shellOnlineSlotRow, treeData]);

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setActionRow(null);
  };

  // Helper: Check if PartTypeId is a burner component type (307-310)
  const isBurnerComponentPartType = (
    partTypeId: number | undefined,
  ): boolean => {
    if (!partTypeId) return false;
    return (
      partTypeId === PART_TYPE_IDS.BURNER_BLOCK ||
      partTypeId === PART_TYPE_IDS.BURNER_FRONT_FACE ||
      partTypeId === PART_TYPE_IDS.BURNER_WATER_JACKET ||
      partTypeId === PART_TYPE_IDS.BURNER_NOZZLE
    );
  };

  // Helper: Find the parent burner position ID for a burner sub-component
  const findParentBurnerPositionId = (
    row: SidewallTreeRow,
    data: SidewallTreeRow[],
  ): number | null => {
    // First check if ParentBurnerPositionId is explicitly set
    if (row.ParentBurnerPositionId) {
      return row.ParentBurnerPositionId;
    }

    // Otherwise, find parent from tree path
    // Burner sub-components are children of burner panels (positions 3, 5, 9)
    // Their path looks like: ["Sidewall-Online", "Sidewall #1", "Sidewall 3 (#1 Burner)", "Block"]
    // The parent position name is the second-to-last element
    if (row.path && row.path.length >= 2) {
      const parentPositionName = row.path[row.path.length - 2];
      const parentPanel = data.find(
        (r) =>
          r.rowType === "location" &&
          r.EquipmentId === row.EquipmentId &&
          r.PositionName === parentPositionName &&
          (BURNER_POSITIONS as readonly number[]).includes(r.PositionId ?? 0),
      );
      if (parentPanel?.PositionId) {
        return parentPanel.PositionId;
      }
    }

    return null;
  };

  // Render function for sidewall SVG thumbnail in DetailsPanel
  const renderSidewallThumbnail = useCallback(
    (row: SidewallTreeRow, data?: SidewallTreeRow[]): ReactNode => {
      if (!row || !data) return null;

      let sidewallData: SidewallData | null = null;

      if (
        isBurnerSubComponent(row) ||
        isBurnerComponentPartType(row.PartTypeId)
      ) {
        // IMPORTANT: Check burner sub-components BEFORE generic location check
        // because burner sub-components also have rowType === "location"
        // For burner sub-components (Block, Front Face, Water Jacket, Nozzle),
        // highlight the parent burner panel (positions 3, 5, or 9)
        const parentPositionId = findParentBurnerPositionId(row, data);

        if (parentPositionId) {
          const parentPanel = data.find(
            (r) =>
              r.rowType === "location" &&
              r.EquipmentId === row.EquipmentId &&
              r.PositionId === parentPositionId,
          );

          if (parentPanel) {
            const highlightedLocation: SidewallTreeRow = {
              ...parentPanel,
              HasAssignedPart: true,
            };
            sidewallData = {
              sidewallId: row.EquipmentId,
              sidewallName: row.EquipmentName ?? "",
              isOnline: row.Status === "Online",
              heats: row.Heats ?? 0,
              totalHeats: row.TotalHeats ?? 0,
              locations: [highlightedLocation],
            };
          }
        }
      } else if (isShellRow(row)) {
        // For shell rows (Upper Shell / Lower Shell), show all their child positions
        // Upper Shell (type 306) -> children are positions 1-12
        // Lower Shell (type 311) -> children are positions 13-17
        // Use PartTypeId instead of PositionId because shells in Spares have different PositionIds
        const isUpperShell = row.PartTypeId === PART_TYPE_IDS.UPPER_SHELL;

        // Filter child positions based on shell type
        const childPositionFilter = isUpperShell
          ? isUpperShellChild
          : isLowerShellChild;

        // Get all child positions for this shell
        const childLocations = data.filter(
          (r) =>
            r.rowType === "location" &&
            r.EquipmentId === row.EquipmentId &&
            r.PositionId != null &&
            childPositionFilter(r.PositionId),
        );

        // Create the shell highlight + child positions
        const shellHighlight: SidewallTreeRow = {
          ...row,
          HasAssignedPart: true,
        };

        sidewallData = {
          sidewallId: row.EquipmentId,
          sidewallName: row.EquipmentName ?? "",
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations: [shellHighlight, ...childLocations],
        };
      } else if (row.rowType === "location") {
        // For regular location (panels, not burner sub-components), highlight just this position
        const highlightedLocation: SidewallTreeRow = {
          ...row,
          HasAssignedPart: true,
        };
        sidewallData = {
          sidewallId: row.EquipmentId,
          sidewallName: row.EquipmentName ?? "",
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations: [highlightedLocation],
        };
      }

      if (!sidewallData) return null;

      // Shells get larger thumbnail (200), locations with parts get 160, empty positions get 180
      const size = isShellRow(row) ? 200 : row.HasAssignedPart ? 160 : 180;
      return <SidewallSvgThumbnail sidewallData={sidewallData} size={size} />;
    },
    [],
  );

  return (
    <>
      <HelmetDexteel title="Sidewall & Burner" />
      <LazyBackDropLoading open={isLoadingTree || isMutating} />

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Sidewall & Burner Tracking
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ViewToggle view={viewMode} onChange={handleViewModeChange} />
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetchTree()}
                disabled={isLoadingTree}
                color="primary"
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Group
          id="sidewall-panels"
          orientation="horizontal"
          onLayoutChanged={onLayoutChanged}
          groupRef={groupRef}
          defaultLayout={defaultLayout}
          style={{ height: "calc(100vh - 150px)" }}
        >
          <Panel id="tree" minSize={20}>
            {viewMode === "tree" ? (
              <SidewallTreePanel
                treeData={treeData}
                selectedRow={selectedRow}
                isLoading={isLoadingTree}
                onRowSelect={handleRowSelection}
                onSetOnline={handleSetOnlineAction}
                onSetOffline={handleSetOfflineAction}
                onInstall={handleInstallAction}
                onUninstall={handleUninstallAction}
                onRepair={handleRepairAction}
                onNewSparePart={handleNewSparePartAction}
                onEditPart={handleEditPartAction}
                onSetInitialMetric={handleSetInitialMetricAction}
                onCorrectDates={handleCorrectDatesAction}
                onDeleteSparePart={handleDeleteSparePartAction}
              />
            ) : (
              <SidewallMapPanel
                treeData={treeData}
                selectedRow={selectedRow}
                activeBurnerChildRow={
                  // Only override 3D highlight for Water Jacket which has its own mesh.
                  // Block/FrontFace/Nozzle share the burner host mesh, so they use the parent's highlight.
                  activeBurnerChildRow?.PositionId === WATER_JACKET_POSITION_ID
                    ? activeBurnerChildRow
                    : null
                }
                onSelectRow={handleRowSelection}
                onSetOnline={handleSetOnlineFromMap}
                onSetOffline={handleSetOfflineFromMap}
                onInstall={handleInstallAction}
                onUninstall={handleUninstallAction}
                onRepair={handleRepairAction}
                onNewSparePart={handleNewSparePartAction}
                onEditPart={handleEditPartAction}
                onSetInitialMetric={handleSetInitialMetricAction}
                isLoading={isLoadingTree}
              />
            )}
          </Panel>

          <ResizablePanelSeparator onDoubleClick={resetLayout} />

          <Panel id="details" minSize={25}>
            {viewMode === "map" &&
            (selectedRow?.rowType === "container" ||
              selectedRow?.rowType === "sparesContainer") ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Part
                </Typography>
              </Box>
            ) : selectedRow?.rowType === "container" ||
              selectedRow?.rowType === "sparesContainer" ? (
              selectedRow?.ContainerId &&
              isSpareContainer(selectedRow.ContainerId) ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: "4px",
                    backgroundColor: "background.paper",
                    gap: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Select a part or create a new one
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleNewSparePartAction(selectedRow)}
                  >
                    New Spare Part
                  </Button>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: "4px",
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    Please select a Part
                  </Typography>
                </Box>
              )
            ) : isBurnerParentInMapMode ? (
              // Burner host selected in 3D view - show component selector with details
              <Box
                ref={containerRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    ...(dragHeight != null
                      ? { height: dragHeight, overflow: "auto" }
                      : {}),
                  }}
                >
                  {/* Details section: SVG + Radio Selector + Part Info */}
                  <Paper
                    elevation={2}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        {`Details - ${selectedRow?.EquipmentName ?? ""} / ${selectedRow?.PositionName ?? ""}`}
                      </Typography>
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          p: 1,
                          border: (theme) =>
                            `1px solid ${theme.palette.divider}`,
                          borderRadius: "4px",
                          overflow: "auto",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            height: "100%",
                            flexDirection: "row",
                          }}
                        >
                          {/* Left side: SVG thumbnail + action buttons */}
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 2,
                            }}
                          >
                            {renderSidewallThumbnail(selectedRow, treeData)}
                            {activeBurnerChildRow?.HasAssignedPart ? (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  startIcon={<LinkOffIcon />}
                                  onClick={() =>
                                    activeBurnerChildRow &&
                                    handleUninstallAction(activeBurnerChildRow)
                                  }
                                >
                                  Uninstall
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="warning"
                                  size="small"
                                  startIcon={<BuildIcon />}
                                  onClick={() =>
                                    activeBurnerChildRow &&
                                    handleRepairAction(activeBurnerChildRow)
                                  }
                                >
                                  Repair
                                </Button>
                              </Box>
                            ) : (
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<LinkIcon />}
                                onClick={() =>
                                  activeBurnerChildRow &&
                                  handleInstallAction(activeBurnerChildRow)
                                }
                              >
                                Install
                              </Button>
                            )}
                          </Box>
                          {/* Right side: Radio selector + Part Info fields */}
                          <Box
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            <BurnerComponentSelector
                              selectedPositionId={burnerChildPositionId}
                              onChange={handleBurnerChildChange}
                              components={burnerSelectableComponents}
                            />
                            {activeBurnerChildRow?.HasAssignedPart ? (
                              <Grid container spacing={1}>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Serial Number"
                                    value={
                                      activeBurnerChildRow?.PartInfo
                                        ?.SerialNumber ?? ""
                                    }
                                    disabled
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Make"
                                    value={
                                      activeBurnerChildRow?.PartInfo?.Make ?? ""
                                    }
                                    disabled
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Model"
                                    value={
                                      activeBurnerChildRow?.PartInfo?.Model ??
                                      ""
                                    }
                                    disabled
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label="Vendor"
                                    value={
                                      activeBurnerChildRow?.PartInfo?.Vendor ??
                                      ""
                                    }
                                    disabled
                                  />
                                </Grid>
                              </Grid>
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                  {/* Metrics section */}
                  <MetricsPanel
                    section="sidewall"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    selectedRow={activeBurnerChildRow}
                    uninstalledOn={null}
                    isSpareContainer={isSpareContainer}
                  />
                </Box>

                <VerticalDragSeparator
                  onMouseDown={handleDragStart}
                  onDoubleClick={resetDragHeight}
                />

                {/* History section */}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <HistoryPanel
                    section="sidewall"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    getTaskMetadataFn={getTaskMetadata}
                    transformHistory={(rows) =>
                      transformHistoryAPIToEntries(
                        rows as unknown as PartDetailsHistoryAPI[],
                      )
                    }
                    selectedRow={activeBurnerChildRow}
                    isSpareContainer={isSpareContainer}
                  />
                </Box>
              </Box>
            ) : selectedRow?.HasChildren === true &&
              !isShellType(selectedRow?.PartTypeId) ? (
              // Tree view fallback: Burner parent selected but children not auto-expanded yet
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: "4px",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  Please select a Child Part
                </Typography>
              </Box>
            ) : (
              <Box
                ref={containerRef}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    ...(dragHeight != null
                      ? { height: dragHeight, overflow: "auto" }
                      : {}),
                  }}
                >
                  <DetailsPanel
                    treeData={treeData}
                    selectedRow={selectedRow}
                    onInstall={handleInstallAction}
                    onUninstall={handleUninstallAction}
                    onRepair={handleRepairAction}
                    onNewSparePart={handleNewSparePartAction}
                    isSpareContainer={isSpareContainer}
                    renderThumbnail={renderSidewallThumbnail}
                  />
                  <MetricsPanel
                    section="sidewall"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    selectedRow={selectedRow}
                    uninstalledOn={
                      selectedEquipmentMasterData?.UninstalledOn
                        ? new Date(selectedEquipmentMasterData.UninstalledOn)
                        : null
                    }
                    onlineEquipmentInstalledOn={
                      selectedRow?.PartTypeId
                        ? (onlineInstalledOnByType[selectedRow.PartTypeId] ??
                          null)
                        : null
                    }
                    isSpareContainer={isSpareContainer}
                  />
                </Box>

                <VerticalDragSeparator
                  onMouseDown={handleDragStart}
                  onDoubleClick={resetDragHeight}
                />

                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <HistoryPanel
                    section="sidewall"
                    partDetailsParams={partDetailsParams}
                    queryFn={getPartDetails}
                    getTaskMetadataFn={getTaskMetadata}
                    transformHistory={(rows) =>
                      transformHistoryAPIToEntries(
                        rows as unknown as PartDetailsHistoryAPI[],
                      )
                    }
                    selectedRow={selectedRow}
                    isSpareContainer={isSpareContainer}
                  />
                </Box>
              </Box>
            )}
          </Panel>
        </Group>
      </Box>

      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />

      {/* Install Part Modal */}
      <InstallPartModal
        open={installModalOpen}
        selectedRow={actionRow}
        treeData={treeData}
        onClose={handleCloseInstallModal}
        getParentPartId={(row) => row.BurnerPanelPartId ?? row.EquipmentId}
        onInstallNewPart={handleInstallPart}
        onInstallExistingPart={handleInstallExistingPart}
        occupantPartId={actionRow?.L2PartId ?? null}
      />

      {/* Notes Modal for Uninstall/Repair */}
      <NotesModal
        open={notesModalOpen}
        title={modalAction === "uninstall" ? "Uninstall Part" : "Repair Part"}
        confirmLabel={modalAction === "uninstall" ? "Uninstall" : "Repair"}
        confirmColor={modalAction === "uninstall" ? "error" : "warning"}
        partId={actionRow?.L2PartId ?? null}
        action={modalAction ?? "uninstall"}
        onClose={handleCloseNotesModal}
        onConfirm={handleNotesModalConfirm}
      />

      {/* New Spare Part Modal */}
      <NewSparePartModal
        open={newSparePartModalOpen}
        containerName={actionRow?.ContainerName ?? ""}
        onClose={handleCloseNewSparePartModal}
        onConfirm={handleNewSparePartConfirm}
      />

      {/* Edit Part Modal */}
      <EditPartModal
        open={editPartModalOpen}
        selectedRow={actionRow}
        onClose={handleCloseEditPartModal}
        onConfirm={handleEditPartConfirm}
      />

      {/* Set Initial Metric Modal */}
      <SetInitialMetricModal
        open={setInitialMetricModalOpen}
        partName={
          actionRow?.EquipmentName ??
          actionRow?.L2PartName ??
          actionRow?.PositionName ??
          null
        }
        currentPartialValue={actionRow?.InitialAbsoluteValue ?? 0}
        showApplyToChildren={actionRow?.HasChildren === true}
        isLoading={setInitialMetricMutation.isPending}
        onClose={handleCloseSetInitialMetricModal}
        onConfirm={handleSetInitialMetricConfirm}
      />

      {/* Correct Dates Modal */}
      <CorrectDatesModal
        open={correctDatesModalOpen}
        partId={correctDatesPartId}
        partName={correctDatesPartName}
        onClose={handleCloseCorrectDatesModal}
        onSave={handleCorrectDatesSave}
      />

      {/* Set Sidewall Status Modal */}
      <SetEquipmentStatusModal
        open={statusModalOpen}
        equipmentName={actionRow?.EquipmentName ?? null}
        equipmentType="Sidewall"
        action={statusModalAction}
        partId={actionRow?.EquipmentId ?? null}
        currentOnlineEquipmentName={
          treeDataRef.current.find(
            (row) =>
              isShellRow(row) &&
              row.Status === "Online" &&
              row.EquipmentId !== actionRow?.EquipmentId &&
              row.PartTypeId === actionRow?.PartTypeId,
          )?.EquipmentName ?? null
        }
        occupantPartId={
          statusModalAction === "online"
            ? (treeDataRef.current.find(
                (row) =>
                  isShellRow(row) &&
                  row.Status === "Online" &&
                  row.EquipmentId !== actionRow?.EquipmentId &&
                  row.PartTypeId === actionRow?.PartTypeId,
              )?.EquipmentId ?? null)
            : null
        }
        locationLastTimestamp={
          statusModalAction === "online" ? shellLocationTimestamp : null
        }
        isLoading={setOnlineMutation.isPending || setOfflineMutation.isPending}
        onClose={handleCloseStatusModal}
        onConfirm={handleStatusModalConfirm}
      />

      {/* Set Shell Online (slot → pick shell) Modal */}
      {shellOnlineSlotRow && shellOnlineModalOpen ? (
        <SetShellOnlineModal
          open={shellOnlineModalOpen}
          slotName={shellOnlineSlotRow.ContainerName ?? "Slot"}
          slotPartId={shellOnlineSlotRow.ContainerId ?? 0}
          availableShells={availableSpareShells}
          locationLastTimestamp={shellLocationTimestamp}
          occupantPartId={null}
          isLoading={
            setOnlineMutation.isPending || setOfflineMutation.isPending
          }
          onClose={handleCloseShellOnlineModal}
          onConfirm={handleSetShellOnlineConfirm}
        />
      ) : null}

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        open={deleteModalOpen}
        title="Delete Spare Part"
        message={`Are you sure you want to delete "${actionRow?.L2PartName ?? "this spare part"}"? This action cannot be undone.`}
        isLoading={deleteSpareMutation.isPending}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteSparePartConfirm}
      />

      {/* No Permission Modal */}
      <NoPermissionModal
        open={noPermissionModalOpen}
        actionName={noPermissionAction}
        onClose={() => setNoPermissionModalOpen(false)}
      />

      {/* Error Modal for mutation/query errors */}
      <ErrorModal error={errorMessage} onHide={() => setErrorMessage("")} />
    </>
  );
};
